/** The built-in organisation roles (spec/07 §6.2). A custom role is any other name. */
export const ORGANIZATION_ROLES = [
    'owner',
    'manager',
    'cashier',
    'floor',
    'kitchen',
    'technician',
    'accountant',
];
/** The platform roles (spec/07 §7). They are held on the user, not on a membership. */
export const PLATFORM_ROLES = ['hq_owner', 'hq_admin', 'hq_finance', 'hq_support', 'hq_content', 'maintainer'];
export function isOrganizationRole(value) {
    return ORGANIZATION_ROLES.includes(value);
}
export function isPlatformRole(value) {
    return typeof value === 'string' && PLATFORM_ROLES.includes(value);
}
/**
 * Read an organisation's audit log (spec/07 §11, F-ORG-14) — and, at platform scope, every
 * organisation's (F-SUP-06). Gates `GET /v1/organizations/{o}/audit`, its export, and
 * `GET /v1/hq/audit` with its export.
 */
export const AUDIT_READ = 'audit:read';
/**
 * A venue's profile (F-ORG-01, spec/07 §6.3 `venues`). Read gates the venue list and page and reading
 * an upload; create, update and archive gate what their names say, and update also gates uploading a
 * venue's photo — the first half of changing its photos.
 */
export const VENUES_READ = 'venues:read';
export const VENUES_CREATE = 'venues:create';
export const VENUES_UPDATE = 'venues:update';
export const VENUES_ARCHIVE = 'venues:archive';
/**
 * HQ's "Organisations and venues" area (spec/14 §3, spec/07 §6.3 `hq`). Gates setting a venue's
 * Featured, Partner and Sponsored flags (F-ORG-01 rule 6), and onboarding an organisation: creating
 * it and inviting its owner, inviting an owner to one that has none, and listing and reading the
 * organisations' records (F-HQ-02, spec/14 §3.1).
 */
export const HQ_ORGANIZATIONS = 'hq:organizations';
/**
 * Stations (spec/07 §6.3 `stations`). `read` gates listing a venue's station classes and stations and
 * the live floor snapshot; `manage` creating and editing them. The station drawer's live-ops actions
 * are their own codes (spec/13 §3.3, the live floor, F-STN-16 … F-STN-18):
 *
 *   stations:power        restart, shut down, sleep or wake a station (F-STN-17) —
 *                         `POST …/stations/{stationId}/power`
 *   stations:maintenance  put a station into, or take it out of, maintenance mode (F-STN-18) —
 *                         `POST …/stations/{stationId}/maintenance`
 *
 * Each is issued as a station command through the realtime gateway (spec/09 §5.1): the api writes the
 * `station_command` row, the gateway delivers it, the station acks. Every one is audited (spec/07 §11).
 */
export const STATIONS_READ = 'stations:read';
export const STATIONS_MANAGE = 'stations:manage';
export const STATIONS_POWER = 'stations:power';
export const STATIONS_MAINTENANCE = 'stations:maintenance';
/**
 * Lockdown recovery and maintenance during play (spec/07 §6.3 `stations`, spec/11 §4.3, §7; F-LCK-06, F-LCK-09):
 *
 *   stations:recovery                issue the one-time recovery code `station-cli undo` needs on the PC —
 *                                    `POST …/stations/{stationId}/recovery-code`. Undoing the lockdown turns a
 *                                    locked PC back into an open one, so it is the owner's and the manager's only.
 *   stations:maintenance_in_session  put a station into maintenance while a player's session runs on it (the
 *                                    player is warned) — beside `stations:maintenance`, which alone is refused
 *                                    while a session is live. The owner's and the manager's.
 */
export const STATIONS_RECOVERY = 'stations:recovery';
export const STATIONS_MAINTENANCE_IN_SESSION = 'stations:maintenance_in_session';
/**
 * Messaging the player at a station (spec/07 §6.3 `stations`, spec/05 §7 F-SES-20). Gates
 * `POST …/sessions/{sessionId}/message` — a short line shown on the station as a kiosk toast and the
 * in-game overlay, optionally requiring an acknowledgement. Floor work, not a change to the station's
 * definition, so it is its own code rather than `stations:manage`: the roles that work the live floor
 * hold it (manager, cashier, floor, technician) without being able to redefine a station.
 */
export const STATIONS_MESSAGE = 'stations:message';
/**
 * Clearing a station's health alerts by hand (spec/07 §6.3 `stations`, spec/13 §3.3; F-STN-11, 2026-10-02). Gates
 * `POST …/stations/{stationId}/alerts/clear` — clear one alert or all of a station's open alerts, or snooze them (hide
 * them from the badge and "Needs attention" for a few hours), with an optional note; audited. A condition the station
 * still reports opens again, so a clear never silences a real problem. Its own code rather than `stations:manage`
 * (redefining a station) or `stations:read`: the people who fix the PCs hold it — the manager and the technician; the
 * owner holds everything.
 */
export const STATIONS_ALERTS_CLEAR = 'stations:alerts_clear';
/**
 * The floor (spec/07 §6.3 `floor`): `read` gates the floor plan and the zones, `arrange` saving the
 * plan and creating or editing a zone (spec/13 §3.4, F-ORG-03 rule 7).
 */
export const FLOOR_READ = 'floor:read';
export const FLOOR_ARRANGE = 'floor:arrange';
/**
 * The staff screen (spec/07 §6.3 `members`, §6.5; F-IAM-08). Each gates the routes in
 * `routes/members.ts` whose contract names it:
 *
 *   members:read     the member list, one member, the roles with their permissions and limits, the
 *                    invitations
 *   members:invite   inviting someone, and revoking an invitation
 *   members:update   which venues a member works at; resetting a member's staff PIN, and turning a
 *                    device into a shared counter device (F-IAM-10, `routes/staff-pin.ts`)
 *   members:roles    which role a member holds, the limits a role carries (F-ORG-13), and making,
 *                    changing and deleting custom roles (F-IAM-11)
 *   members:remove   taking someone off the staff, and suspending or reinstating them (F-ORG-13)
 *
 * Whoever holds them is still bound by the rule of §6.3: a role is granted, changed or taken away
 * only when it is **below** the actor's own — its permissions a strict subset of theirs — an owner
 * is touched only by an owner, and nobody gives a role a limit above their own role's (§6.4).
 */
export const MEMBERS_READ = 'members:read';
export const MEMBERS_INVITE = 'members:invite';
export const MEMBERS_UPDATE = 'members:update';
export const MEMBERS_ROLES = 'members:roles';
export const MEMBERS_REMOVE = 'members:remove';
/**
 * Banning a player (spec/07 §3.5, F-IAM-07). Gates banning and lifting a ban — at one venue, through
 * `/venues/{venueId}/players/{playerId}/…`, and across the organisation — and reading a player's
 * bans, which is the only way a ban's internal reason leaves the server.
 *
 * Holding it is not the whole rule (spec/07 §3.5): a member whose venue scope is some venues bans
 * at those venues only and sees only their bans and the organisation's; an organisation ban needs a
 * member who works at every venue.
 */
export const PLAYERS_BAN = 'players:ban';
/**
 * Staff document ID checks (spec/07 §10, F-CMP-02). Gates recording a check
 * (`POST …/players/{playerId}/id-checks`), the ID checks tab that lists them, and viewing a stored
 * ID photo — which also writes an audit row every time (spec/07 §10: "Viewing a stored ID photo needs
 * `players:id_check` and is audited").
 */
export const PLAYERS_ID_CHECK = 'players:id_check';
/**
 * HQ's "Players" area (spec/14 §10, spec/07 §6.3 `hq`). Gates the platform ban and its lifting
 * (F-IAM-07): a person banned from the Platform cannot sign in anywhere, and every session they hold
 * is revoked.
 */
export const HQ_PLAYERS = 'hq:players';
/**
 * Reading HQ's "Players" area (spec/14 §10, F-HQ-28): the platform search and one player's platform
 * record, contact details in full. The read half of `hq:players`, split from it by the product owner
 * (2026-09-22) so that support can find a person without being able to ban them. Spelled as a
 * permission is spelled here — `resource:action`, one colon, a multi-word action joined by `_` as
 * `players:read_contact` is — so `hq:players_read`, not `hq:players:read`.
 */
export const HQ_PLAYERS_READ = 'hq:players_read';
/**
 * A franchisor's Brand HQ oversight of its own subtree (ADR-0019 Phase 2, F-ORG-17, spec/07 §6.3
 * `franchise`). Checked at the **franchisor** organisation's scope, it gates the read-only Brand HQ
 * endpoints — listing the organisations whose `franchisorOrganizationId` is this org (its direct
 * franchisees) and reading one franchisee's venues (`routes/franchisees.ts`). It is a **bounded**
 * access rule, not the Provider's platform-wide bypass: a franchisor sees only its own direct
 * franchisees and their venues, never a sibling's, never an unrelated organisation's, and every
 * cross-organisation read it makes is audited in the franchisee's own log, exactly as the Provider's
 * platform-scope read is (spec/07 §7, §11.2).
 *
 * Held by the `owner` by rule (the owner holds every organisation permission, §6.2); no built-in role
 * below owner holds it, because overseeing the brand's franchisees is an ownership concern, not
 * day-to-day venue work — an owner who wants a manager to have it builds a custom role.
 *
 * **`franchise:manage` is deliberately not defined yet.** It is the later, write half — a franchisor
 * acting on its franchisees (central-billing toggle, ADR-0019 Phase 5) — and this codebase adds a
 * permission in the same change as the route it gates, never before (`registry.test.ts`: a declared
 * permission that gates nothing fails the build). This phase is read-first; `franchise:manage` lands
 * with the first cross-org write endpoint it guards.
 */
export const FRANCHISE_READ = 'franchise:read';
/**
 * The settings screens (spec/07 §6.3 `settings`, spec/13 §13; F-ORG-02).
 *
 * `settings:read` gates reading an organisation's or a venue's settings, and is the floor for
 * changing one, because a change is made from that screen. Each other code is a settings **group**
 * permission: a setting names one as its `editPermission`, and `PUT …/settings` refuses a change to
 * a key whose permission the caller does not hold (`x-clavian-setting-permission` on the route).
 *
 * Only the groups a registered setting actually names are here — a group permission that no setting
 * uses gates nothing — and the list grows as features declare settings:
 *
 *   settings:all            `staff.require2faRoles`: the ones nobody should turn by accident
 *   settings:notifications  `auth.otpChannel`, `notifications.otpChannelOrder`
 *   settings:locker         `venue.powerSchedule`
 *   settings:players        `compliance.idCheckRequired`, `compliance.acceptedIdMethods`,
 *                           `compliance.idCheckValidityMonths` — the ID-check rule (spec/07 §6.3)
 *   settings:kiosk          `org.branding`: the organisation's colours on its venues' lock screens
 *   settings:hardware       `hardware.drawerKickOnCash`, and the venue's printers — adding, editing and archiving
 *                           them, their purpose routing, a test print, and the recent print jobs (spec/13 §11,
 *                           F-OPS-08). The manager and the technician hold it ("printer and hardware settings",
 *                           spec/07 §6.2)
 */
export const SETTINGS_READ = 'settings:read';
export const SETTINGS_ALL = 'settings:all';
export const SETTINGS_NOTIFICATIONS = 'settings:notifications';
export const SETTINGS_LOCKER = 'settings:locker';
export const SETTINGS_HARDWARE = 'settings:hardware';
export const SETTINGS_PLAYERS = 'settings:players';
export const SETTINGS_KIOSK = 'settings:kiosk';
/**
 * The Sessions settings group (spec/13 §13). Gates changing the grace, comp and pay-as-you-go
 * knobs the session spine reads (`sessions.graceSeconds`, `sessions.compMaxMinutesPerAction`,
 * `sessions.payg*`, F-SES-10/-26). An operational group, so the manager holds it as well as the owner.
 */
export const SETTINGS_SESSIONS = 'settings:sessions';
/**
 * The Payments settings group (spec/13 §13, spec/06 §11). Gates changing the wallet's money knobs —
 * `wallet.enabled`, the top-up limits (`wallet.minTopupAmount`, `maxTopupAmount`, `maxBalanceAmount`),
 * the top-up offers and the spend order — and, until a Tax settings group lands with the tax engine
 * (F-PAY-11), the wallet's tax point (`tax.walletTopupTaxPoint`). Money-sensitive organisation
 * configuration, so it stays the owner's by default (F-WAL-01/-02/-04).
 */
export const SETTINGS_PAYMENTS = 'settings:payments';
/**
 * HQ's platform settings (spec/14 §14, spec/07 §6.3 `hq:settings`): the platform defaults, and the
 * locks at platform and at organisation scope (spec/03 §9). Gates every `/v1/hq/…/settings` route.
 */
export const HQ_SETTINGS = 'hq:settings';
/**
 * HQ's payments oversight for one organisation (spec/14 §5, spec/06 §9.5, ADR-0061; F-PAY-05, F-HQ-13): reading
 * its online-payments setup and **onboarding or correcting its payee** at the payment provider — the bank account,
 * PAN, GSTIN and contact its share of every online payment is paid out to — and asking the provider for the
 * payee's status. An HQ **area** permission like `hq:organizations`. Held by `hq_owner` alone for now (by holding
 * all): it moves where an organisation's money is paid, so no other platform role holds it until the owner says
 * so (spec/07 §7 lists payout oversight under `hq_finance`; that grant is an open question in ADR-0061).
 */
export const HQ_PAYMENTS = 'hq:payments';
/**
 * HQ's "Subscriptions" area (spec/14 §4, spec/07 §6.3 `hq:subscriptions`; F-BOOT-17, F-HQ-07, F-HQ-10): today the
 * provider-priced **add-ons** — the catalogue (`GET/POST/PATCH /v1/hq/addons`: Clavian Boot and its monthly price per
 * PC) and one organisation's (`GET/POST/PATCH /v1/hq/organizations/{o}/addons`: attach with the licensed PC count,
 * change the count, cancel). An attached add-on grants entitlements (`boot`, `maxBootPcs`), so this decides what a
 * venue may use and what it is charged. An HQ **area** permission like `hq:payments`, and like it held by `hq_owner`
 * alone (by holding all) until the owner extends it: spec/07 §7 lists subscriptions under `hq_admin` and
 * `hq_finance`, which do **not** hold it yet (an open question on F-BOOT-17).
 */
export const HQ_SUBSCRIPTIONS = 'hq:subscriptions';
/**
 * HQ's "Catalogue" area (spec/14 §6, spec/07 §7 `hq_content` "Game catalogue"; F-CAT-01, F-CAT-07).
 * The curated global game catalogue — HQ's mirror of an IGDB base game into our own `game` table.
 * Gates searching IGDB for base games, mirroring one by its `igdbId`, and listing what has been
 * mirrored (`GET /v1/hq/catalogue/search`, `POST` and `GET /v1/hq/catalogue/games`). An HQ **area**
 * permission, gating read and write of the area like `hq:organizations` does — not one resource with
 * several actions, which is the organisation catalogue's shape (`library`, spec/07 §6.3).
 */
export const HQ_CATALOG = 'hq:catalog';
/**
 * HQ's "Station releases" area (spec/14 §12, spec/10 §8; F-STN-12): register a signed station release, set each
 * channel's staged rollout (target version, percentage, halt) and read the fleet's client versions. An HQ **area**
 * permission like `hq:catalog`. Held by `hq_admin` (and `hq_owner` by holding all): a rollout reaches every venue's PCs.
 */
export const HQ_RELEASES = 'hq:releases';
/**
 * HQ's sales pipeline (spec/14 §16, spec/07 §7; F-HQ-39): leads, their timeline, message templates, import, the daily
 * queue and the funnel. An HQ **area** permission like `hq:catalog`. Held by `hq_admin` (whose area is onboarding
 * organisations) and `hq_owner` by holding all. Converting a lead into an organisation also needs `hq:organizations`.
 */
export const HQ_LEADS = 'hq:leads';
/**
 * A venue's library (spec/07 §6.3 `library`, spec/13 §9.1; F-CAT-02). The titles a venue offers,
 * drawn from the platform catalogue (`hq:catalog`'s `game`), each placed on station classes and in a
 * kiosk category. Each gates the routes in `routes/library.ts` whose contract names it:
 *
 *   library:read    the venue's library — the list, and one entry
 *   library:manage  add a catalogue game to the library, edit an entry (its classes, category,
 *                   featured flag, visibility, order, Install for players), and remove one; and ask the
 *                   venue's PCs to install an app with a provisioning recipe now, on one PC or on every
 *                   PC missing it (F-CAT-09, ADR-0056) — what the Install for players switch already lets
 *                   the same holders have every PC do
 *
 * The `updates` action of the §6.3 `library` resource (F-CAT-06, the SteamCMD/launcher update path)
 * is station work and is not defined yet — this codebase adds a permission in the same change as the
 * route it gates (`registry.test.ts`: a permission that gates nothing fails the build).
 */
export const LIBRARY_READ = 'library:read';
export const LIBRARY_MANAGE = 'library:manage';
/**
 * An organisation's players (spec/13 §8, spec/07 §6.3 `players`; F-ORG-05, F-ORG-06). Each gates the
 * routes in `routes/players.ts` whose contract names it:
 *
 *   players:read          search the organisation's own players and open one: profile basics, tags,
 *                         notes and the history tabs — this organisation's dealings only
 *   players:read_contact  the same answers with the phone number and the email address in full. It
 *                         gates no route of its own: it **unmasks** part of the answer of the routes
 *                         that declare it as `x-clavian-unmasked-by`. Without it the server masks
 *                         both, on every response that carries them (spec/13 §8: "masked unless
 *                         `players:read` includes contact details for the role")
 *   players:notes         write a note or a tag (`PATCH …/players/{playerId}`)
 */
export const PLAYERS_READ = 'players:read';
export const PLAYERS_READ_CONTACT = 'players:read_contact';
export const PLAYERS_NOTES = 'players:notes';
/**
 * Running a session on a station (spec/07 §6.3 `sessions`, spec/05 §7; F-SES-01/-04/-10/-14/-26).
 * Each gates the routes in `routes/sessions.ts` whose contract names it:
 *
 *   sessions:start   staff assign a player to a station (F-SES-04, the only way for a manual
 *                    station) — `POST …/sessions`
 *   sessions:end     end a running session (F-SES-14) — `POST …/sessions/{sessionId}/end`
 *   sessions:read    the session's status, time left and timeline (F-SES-11, F-SES-21) — the two
 *                    `GET` routes
 *   sessions:comp    grant free comp time, with a reason, to a running session (F-SES-16, the comp
 *                    funding source; recovers a session in grace, F-SES-26) — `…/sessions/{id}/comp`.
 *                    It moves value (SEC), so it is second-factor gated and audited; its per-action
 *                    and per-shift minute limits (`sessions.compMax*`) are a role limit (spec/07 §6.4).
 *
 * The kiosk sign-in start (F-SES-01) is on the station's own authenticated surface — the station
 * proves itself with its device token — and is not gated by an organisation permission.
 */
export const SESSIONS_START = 'sessions:start';
export const SESSIONS_END = 'sessions:end';
export const SESSIONS_READ = 'sessions:read';
export const SESSIONS_COMP = 'sessions:comp';
/**
 * Lifts the **setting's** comp caps (`sessions.compMaxMinutesPerAction`, `sessions.compMaxMinutesPerShift`) for its
 * holder, never a limit written on their role (spec/07 §6.4, spec/05 §7; F-SES-16). It gates part of the comp
 * route rather than a route of its own — the route declares it as its limit lift (`x-clavian-unlimited-by`), like a
 * field permission — and the manager holds it by default: "small comps within limits" is the cashier's line
 * (spec/07 §6.2), the manager runs the floor.
 */
export const SESSIONS_COMP_UNLIMITED = 'sessions:comp_unlimited';
/**
 * Locking and unlocking a station's screen from the floor (spec/07 §6.3 `sessions:lock`, spec/13 §3.3;
 * F-STN-16). Gates `POST …/stations/{stationId}/lock`, which issues the `session.lock` / `session.unlock`
 * command through the gateway (spec/09 §6.2). A floor action rather than a machine one, so the roles
 * that run sessions hold it. Audited.
 */
export const SESSIONS_LOCK = 'sessions:lock';
/**
 * Moving a running session to another station (spec/07 §6.3 `sessions:move`, spec/05 §7; F-SES-18).
 * Gates `POST …/sessions/{sessionId}/move`, which previews the warnings a cross-class move raises
 * (passes may not apply, a higher age limit) and, on confirmation, moves the one session row to the
 * new station. A floor action the roles that run sessions hold. Audited.
 */
export const SESSIONS_MOVE = 'sessions:move';
/**
 * Generating an offline code for a station (spec/07 §6.3 `sessions:offline_code`, spec/05 §8.4;
 * F-SES-24). Gates `POST …/stations/{stationId}/offline-codes`, which mints the 8-digit HMAC code staff
 * read out to **start**, **extend** or run **maintenance** on a station while its venue has lost its
 * connection. The code is derived from the station's per-station offline secret and never leaves the
 * server except as the 8 digits (the secret itself is never returned or logged). A floor action the
 * roles that run sessions hold — manager, cashier and floor. Audited (`sessions.offline_code.generated`).
 *
 * Open question flagged on the PR: whether `technician` should also hold it, since spec/11 frames the
 * `maintenance` code as a staff-app unlock the technician performs. The conservative reading here grants
 * it to the front-of-house roles that already start and move sessions, and leaves the technician out
 * pending the owner's call; an owner who wants a technician to generate one builds a custom role.
 */
export const SESSIONS_OFFLINE_CODE = 'sessions:offline_code';
/**
 * Calls for help (spec/07 §6.3 `calls`, spec/05 §11, spec/13 §3.2; F-SES-25). A player raises a call
 * from the kiosk; the front of house sees it in "What needs you" and works it:
 *
 *   calls:read    the venue's open and recent calls — `GET …/venues/{v}/calls` — and the live
 *                 `venue:{v}:calls` channel the gateway gates with it (spec/09 §3)
 *   calls:manage  answer a call ("On my way") and resolve it ("Done") — the two
 *                 `POST …/venues/{v}/calls/{callId}/…` routes. Not money, but a floor action, audited.
 *
 * A player raising or cancelling their own call is on the station's own authenticated surface and
 * carries no organisation permission.
 */
export const CALLS_READ = 'calls:read';
export const CALLS_MANAGE = 'calls:manage';
/**
 * The F&B menu (spec/07 §6.3 `products`, spec/13 §6.1; F-FNB-01, -02, -03). Each gates the routes in
 * `routes/food.ts` whose contract names it:
 *
 *   products:read          list a venue's menu — its categories and products with variants and
 *                          modifiers, with the venue's price and availability applied — and read one
 *   products:manage        create and edit categories and products (with their variants and
 *                          modifiers), and set a venue's price overrides
 *   products:availability  the "sold out" switch — turn a product on or off without editing it, at
 *                          the organisation or for one venue (spec/13 §6.1)
 *
 * The menu carries a price on every product (`_minor` money), so a change to it moves nothing but is
 * audited as configuration (spec/07 §11, CLAUDE.md).
 */
export const PRODUCTS_READ = 'products:read';
export const PRODUCTS_MANAGE = 'products:manage';
export const PRODUCTS_AVAILABILITY = 'products:availability';
/**
 * Placing and reading food orders (spec/07 §6.3 `orders`, spec/06 §8; F-FNB, F-POS-01). The food
 * module owns the F&B order foundation — settlement (tenders, the sale document, the `sales_fnb`
 * ledger posting) is the `commerce`/`payments` build and rides in later ([ADR-0020](../../adr/0020-food-and-kitchen-foundation-scope.md)):
 *
 *   orders:create  place a food order against a session, a station or the counter — `POST …/food-orders`
 *   orders:read    the food order list and one order — the two `GET` routes
 *   orders:void    cancel a placed order before it is settled — `POST …/food-orders/{id}/cancel`
 *                  (the nearest existing action to spec/13 §5's "Cancel (unpaid)"; a supervisory action)
 */
export const ORDERS_CREATE = 'orders:create';
export const ORDERS_READ = 'orders:read';
export const ORDERS_VOID = 'orders:void';
/**
 * The POS depth of the `orders` group (spec/07 §6.3 `orders`, spec/06 §8.4, §8.7; F-POS-05, F-POS-08).
 * Each gates a route on a sale in `commerce/routes.ts`:
 *
 *   orders:discount        apply a manual staff discount to a placed, unpaid sale, capped by the
 *                          effective role limit of `pos.manualDiscountMaxBps` (spec/07 §6.4) — with a
 *                          reason, audited (F-POS-05). `POST …/sales/{id}/discount`.
 *   orders:refund          refund a sale in whole or in part, to a chosen tender, reversing the ledger
 *                          (F-POS-08). `POST …/sales/{id}/refunds`.
 *   orders:refund_approve  approve a refund above the effective limit of `pos.refundApprovalAmount` —
 *                          a *second* member, never the one who asked (F-POS-08).
 *                          `POST …/sales/{id}/refunds/{refundId}/approve`.
 *
 * `orders:discount_unlimited` (the setting-cap lift, spec/07 §6.4) and `orders:on_account`/`orders:export`
 * ride in with the features that read them (on account F-POS-07, export F-POS-14); a permission that
 * gates nothing is not declared yet.
 */
export const ORDERS_DISCOUNT = 'orders:discount';
export const ORDERS_REFUND = 'orders:refund';
export const ORDERS_REFUND_APPROVE = 'orders:refund_approve';
/**
 * Discount codes (spec/07 §6.3 `discounts`, spec/06 §8.4; F-POS-02). The organisation defines codes
 * staff enter at checkout; the group is `read, manage`:
 *
 *   discounts:read    list the organisation's discount codes — `GET …/discount-codes`
 *   discounts:manage  define a code — `POST …/discount-codes`. Audited as configuration.
 *
 * Promotions and coupons (F-POS-03, F-POS-06) are the same group, and join it with their features.
 */
export const DISCOUNTS_READ = 'discounts:read';
export const DISCOUNTS_MANAGE = 'discounts:manage';
/**
 * The kitchen display (spec/07 §6.3 `kitchen`, spec/13 §6.2; F-FNB-04). Each gates the routes in
 * `routes/kitchen.ts` whose contract names it:
 *
 *   kitchen:read    the tickets for a venue's preparation stations — `GET …/kitchen/tickets`
 *   kitchen:update  advance a ticket through its states (accept, ready, delivered) and recall one —
 *                   `POST …/kitchen/tickets/{id}/state`. Audited (spec/07 §11).
 */
export const KITCHEN_READ = 'kitchen:read';
export const KITCHEN_UPDATE = 'kitchen:update';
/**
 * The Food and drinks settings group (spec/13 §13, spec/07 §6.3 `settings:fnb`). Gates changing the
 * F&B knobs — today `fnb.sendToKitchenBeforePayment` (F-FNB-04). An operational group, so the manager
 * holds it as well as the owner.
 */
export const SETTINGS_FNB = 'settings:fnb';
/**
 * Stock (spec/07 §6.3 `inventory`, spec/13 §6.4, ADR-0040; F-FNB-07 … F-FNB-13, 2026-10-07). Each gates the routes in
 * `routes/inventory.ts` whose contract names it; every write is audited, and every write is refused while
 * `inventory.enabled` is off at the venue:
 *
 *   inventory:read      the stock list, an item, its history, receipts, counts, recipes, the status and the alerts
 *   inventory:receive   record a delivery (a receipt)
 *   inventory:count     open a full or spot count, fill it in, submit or cancel it
 *   inventory:waste     record waste (with a reason), staff meals and supply issues — by item or by dish
 *   inventory:adjust    a manual correction to one item, with a note
 *   inventory:manage    stock items and their packs, recipes, suppliers, copying another venue's definitions
 *   inventory:reports   stock value, usage, count variance and margins — and the ₹ figures on the other stock reads
 *                       (average cost, value, a movement's value, a count's variance value), declared there as
 *                       `x-clavian-unmasked-by` and `null` without it
 *   inventory:supplies  receive, count, issue, adjust and add or edit **supply** items only: the routes that admit it
 *                       declare it as their narrower permission (`x-clavian-narrow-permission`), and the handler
 *                       refuses such a caller anything that is not a supply
 */
export const INVENTORY_READ = 'inventory:read';
export const INVENTORY_RECEIVE = 'inventory:receive';
export const INVENTORY_COUNT = 'inventory:count';
export const INVENTORY_WASTE = 'inventory:waste';
export const INVENTORY_ADJUST = 'inventory:adjust';
export const INVENTORY_MANAGE = 'inventory:manage';
export const INVENTORY_REPORTS = 'inventory:reports';
export const INVENTORY_SUPPLIES = 'inventory:supplies';
/**
 * The wallet (spec/07 §6.3 `wallet`, spec/06 §5; F-WAL-01/-02/-06/-07). The full group is
 * `read, topup, adjust, refund, refund_negative`; three codes have endpoints and are enforced:
 *
 *   wallet:topup   add value to a player's wallet at the counter, paid by a tender
 *                  (`POST …/players/{playerId}/wallet/top-ups`, F-WAL-02). A top-up sold through the
 *                  checkout (spec/06 §8.1's `wallet_topup` order line) is additionally gated by
 *                  checkout when the POS lands; until then this is the only door, and it moves money,
 *                  so it is audited.
 *   wallet:adjust  credit or debit a player's wallet cash with a reason, capped by the effective
 *                  role limit of `wallet.adjustMaxAmount` (`POST …/wallet/adjustments`, F-WAL-07,
 *                  spec/07 §6.4). Moves money, so it is audited.
 *   wallet:refund  refund wallet cash to a counter tender, reversing a top-up
 *                  (`POST …/wallet/refunds`, F-WAL-06). Refused when it would overdraw the cash the
 *                  wallet holds. Moves money, so it is audited.
 *
 * Reading a wallet's balance and statement rides `players:read`, the permission that already gates
 * the player's wallet tab (`routes/players.ts`). `wallet:read` lands with a wallet read of its own,
 * and `wallet:refund_negative` — the lift that lets a refund overdraw once the cash has been spent
 * (spec/06 §8.7) — lands with the top-up-lot tracking a same-top-up bonus clawback needs (F-WAL-05),
 * exactly as `orders:discount_unlimited` waits for the endpoint it lifts.
 */
export const WALLET_TOPUP = 'wallet:topup';
export const WALLET_ADJUST = 'wallet:adjust';
export const WALLET_REFUND = 'wallet:refund';
/**
 * Shifts, cash and the day close (spec/07 §6.3 `shifts`, spec/13 §10; F-OPS-01 … F-OPS-07). Each
 * gates the routes in `routes/shifts.ts` whose contract names it:
 *
 *   shifts:own             open a till session with an opening float, read the venue's open shift,
 *                          and close a shift with the blind count and its variance (F-OPS-01, -03,
 *                          -04) — the cashier's "open and close own shift" (spec/07 §6.2)
 *   shifts:read_all        list the venue's shifts and read any one of them with its X/Z report
 *                          (F-OPS-04) — a supervisory read the manager holds
 *   shifts:cash_movements  record a pay-in, a pay-out or a drop to the safe on the open drawer
 *                          (F-OPS-02) — money moves, so it is audited and posts through the ledger
 *   shifts:day_close       review the business day's takings by tender and close it once every shift
 *                          is closed (F-OPS-06) — a closed day can no longer be voided (spec/13 §10)
 *   shifts:close_any       close — or hand over — a shift another member opened (F-OPS-03, -05; 2026-10-01).
 *                          `shifts:own` closes only the caller's own shift; the close route declares this
 *                          as its others' permission (`x-clavian-others-permission`); the manager holds it
 *
 *   shifts:drawer_open     kick the cash drawer outside a cash payment — **Open drawer**
 *                          (`POST …/drawer/open`, F-OPS-09). Refused at the endpoint without it, so nothing
 *                          reaches the desk device; every one is audited with the shift and the member. The
 *                          cashier (the till) and the manager hold it
 */
export const SHIFTS_OWN = 'shifts:own';
export const SHIFTS_READ_ALL = 'shifts:read_all';
export const SHIFTS_CASH_MOVEMENTS = 'shifts:cash_movements';
export const SHIFTS_DAY_CLOSE = 'shifts:day_close';
export const SHIFTS_CLOSE_ANY = 'shifts:close_any';
export const SHIFTS_DRAWER_OPEN = 'shifts:drawer_open';
/**
 * Passes (spec/07 §6.3 `passes`, spec/06 §3; F-PRC-01/-02/-04/-05/-06). A versioned pass product is
 * play time on a station class; a holding is what a player holds. Each gates the routes in
 * `routes/passes.ts` whose contract names it:
 *
 *   passes:read    list and read pass products and holdings — the Passes screen and the player's
 *                  Passes tab
 *   passes:manage  create and edit pass products (editing price, duration, classes or windows makes
 *                  a new version, existing holdings keep theirs, spec/06 §3.1)
 *   passes:grant   staff grant a holding with a reason, at no charge, recorded as a zero-value order
 *                  (spec/06 §3.2). Free value, so audited
 *   passes:refund  refund a holding, returning money by its rules (spec/06 §3.4). Audited
 *   passes:void    void a holding, removing unused seconds and returning no money (spec/06 §3.4).
 *                  Audited
 *
 * **Selling** a pass (a paid holding) rides `orders:create`, the permission that already gates
 * placing an order at the counter (spec/06 §3.2: a holding is created "when an order line of kind
 * `pass` is fulfilled"). No `passes:sell` of its own, so the front desk that takes any order sells a
 * pass without a new grant.
 */
export const PASSES_READ = 'passes:read';
export const PASSES_MANAGE = 'passes:manage';
export const PASSES_GRANT = 'passes:grant';
export const PASSES_REFUND = 'passes:refund';
export const PASSES_VOID = 'passes:void';
/**
 * Pricing (spec/07 §6.3 `pricing`, spec/06 §4; F-PRC-10 … F-PRC-12). Each gates the rate-card routes in
 * `routes/rate-cards.ts`:
 *
 *   pricing:read    read a venue's rate card — the version in force, the ones scheduled and the history
 *   pricing:manage  publish a new version of a venue's rate card (per class, bands, billing unit, minimum, daily
 *                   cap). A money permission: it decides what every pay-as-you-go second costs. Audited
 *
 * The owner holds both (everything); the manager too — "everything operational: prices" (spec/07 §6.2), and it
 * already edits the flat pay-as-you-go rate through `settings:sessions`. The accountant reads.
 */
export const PRICING_READ = 'pricing:read';
export const PRICING_MANAGE = 'pricing:manage';
/**
 * Taking a sale on account (spec/07 §6.3 `orders`, spec/06 §8.6; F-POS-07). The checkout tender that
 * creates a receivable — one of the two doors to on-account, the postpaid session being the other.
 * Registered players only, bounded by the organisation's credit limit and its per-venue cap. Gates
 * `POST …/sales/{id}/on-account`. Held by the front desk (cashier and manager); it moves value, so
 * it is audited and settles through the ledger's `receivable` account.
 */
export const ORDERS_ON_ACCOUNT = 'orders:on_account';
/**
 * Setting a player's on-account credit limit (spec/06 §8.6, spec/07 §6.3 `credit`; F-POS-07). Gates
 * `PUT …/players/{playerId}/credit`. The manager and the owner set limits; a limit change is audited.
 */
export const CREDIT_MANAGE = 'credit:manage';
/**
 * Writing off a player's receivable (spec/06 §8.6, spec/07 §6.3 `credit`; F-POS-07). Gates
 * `POST …/players/{playerId}/receivable/write-off`. It forgives money owed, so it is the owner's
 * alone by default (an owner who wants a manager to hold it builds a custom role); audited, and it
 * posts to the ledger's `write_off` account.
 */
export const CREDIT_WRITE_OFF = 'credit:write_off';
/**
 * Recording a venue's tax registration — its tax mode and GSTIN — by a dated, audited change (spec/06
 * §10.4, spec/07 §6.3; F-PAY-11). Gates `GET/POST …/venues/{v}/tax-registration` and the withdrawal of
 * a scheduled change. A registration decides whether the venue charges GST and which document a paid
 * order gets, so it is the owner's and the accountant's by default (spec/06 §10.4 rule 2): the people
 * who answer for the organisation's tax. A manager runs the floor but does not register the business.
 */
export const TAX_MANAGE = 'tax:manage';
/**
 * Reading the venue's reports (spec/07 §6.3 `reports`, spec/17; F-CAT-04, F-RPT): today the **Games played**
 * report — hours per title, per day and week, and per PC, from the station-detected plays (`game_play`). Not
 * financial (`reports:financial` arrives with the revenue reports) and not an export. The manager ("everything
 * operational … reports", spec/07 §6.2) and the accountant ("read-only reports") hold it; the owner holds
 * everything.
 */
export const REPORTS_READ = 'reports:read';
/**
 * The venue's and the organisation's financial reports (spec/07 §6.3 `reports:financial`, spec/17 §4.1 "financials
 * need `reports:financial`"; F-ANL-01, 2026-10-01): the Reports overview — revenue (M-01), net sales, tax, orders,
 * refunds, wallet and the rest of the canonical metrics — for a venue and by venue across the organisation. Held by
 * the manager (who already closes the day and sees its takings) and the accountant ("read-only reports"); the owner
 * holds everything.
 */
export const REPORTS_FINANCIAL = 'reports:financial';
/**
 * The organisation's subscription, as its owner reads it (spec/07 §6.3 `org:billing`, spec/14 §4.5; F-BOOT-17,
 * F-HQ-07): today the add-ons the Provider has attached — Clavian Boot, priced per licensed PC — with each one's
 * monthly charge and the total. Gates `GET /v1/organizations/{o}/addons`. Read-only: the Provider attaches, changes
 * and cancels add-ons (`hq:subscriptions`). What the organisation pays is the owner's business, so the owner holds it
 * (by holding everything) and no other built-in role does; an owner who wants an accountant to see it builds a custom
 * role.
 */
export const ORG_BILLING = 'org:billing';
/**
 * Clavian Boot (ADR-0063, spec/25 §14, spec/07 §6.3 `boot`). Every route also needs the organisation's `boot`
 * entitlement (`402 entitlement-required` without it).
 *
 *   boot:view        the venue's boot servers, images and versions, boot PCs and their health — the console's Boot section
 *   boot:manage      pair and unpair a boot server, make and change images (new, import, restore, merge), add, assign
 *                    and remove boot PCs, and restart idle PCs onto a new version
 *   boot:edit_image  start, publish and discard an edit boot (spec/25 §10)
 *   boot:wipe        force-wipe a PC's write-back, or the shader caches of one PC, one GPU family or the fleet
 *
 * spec/25 writes the third `boot:edit-image`; a permission's action is snake_case (`PERMISSION_PATTERN`), so it is
 * `boot:edit_image` here and in spec/07 and spec/25.
 */
export const BOOT_VIEW = 'boot:view';
export const BOOT_MANAGE = 'boot:manage';
export const BOOT_EDIT_IMAGE = 'boot:edit_image';
export const BOOT_WIPE = 'boot:wipe';
/** Every permission checked at organisation or venue scope. Grows with the endpoints; see above. */
export const ORGANIZATION_PERMISSIONS = [
    AUDIT_READ,
    VENUES_READ,
    VENUES_CREATE,
    VENUES_UPDATE,
    VENUES_ARCHIVE,
    FRANCHISE_READ,
    STATIONS_READ,
    STATIONS_MANAGE,
    STATIONS_POWER,
    STATIONS_MAINTENANCE,
    STATIONS_RECOVERY,
    STATIONS_MAINTENANCE_IN_SESSION,
    STATIONS_MESSAGE,
    STATIONS_ALERTS_CLEAR,
    FLOOR_READ,
    FLOOR_ARRANGE,
    LIBRARY_READ,
    LIBRARY_MANAGE,
    MEMBERS_READ,
    MEMBERS_INVITE,
    MEMBERS_UPDATE,
    MEMBERS_ROLES,
    MEMBERS_REMOVE,
    PLAYERS_BAN,
    SETTINGS_READ,
    SETTINGS_ALL,
    SETTINGS_NOTIFICATIONS,
    SETTINGS_LOCKER,
    SETTINGS_HARDWARE,
    PLAYERS_READ,
    PLAYERS_READ_CONTACT,
    PLAYERS_NOTES,
    PLAYERS_ID_CHECK,
    SETTINGS_PLAYERS,
    SETTINGS_KIOSK,
    SETTINGS_SESSIONS,
    SETTINGS_PAYMENTS,
    SESSIONS_START,
    SESSIONS_END,
    SESSIONS_READ,
    SESSIONS_COMP,
    SESSIONS_COMP_UNLIMITED,
    SESSIONS_LOCK,
    SESSIONS_MOVE,
    SESSIONS_OFFLINE_CODE,
    CALLS_READ,
    CALLS_MANAGE,
    PRODUCTS_READ,
    PRODUCTS_MANAGE,
    PRODUCTS_AVAILABILITY,
    ORDERS_CREATE,
    ORDERS_READ,
    ORDERS_VOID,
    ORDERS_DISCOUNT,
    ORDERS_REFUND,
    ORDERS_REFUND_APPROVE,
    DISCOUNTS_READ,
    DISCOUNTS_MANAGE,
    KITCHEN_READ,
    KITCHEN_UPDATE,
    SETTINGS_FNB,
    WALLET_TOPUP,
    WALLET_ADJUST,
    WALLET_REFUND,
    SHIFTS_OWN,
    SHIFTS_READ_ALL,
    SHIFTS_CASH_MOVEMENTS,
    SHIFTS_DAY_CLOSE,
    SHIFTS_CLOSE_ANY,
    SHIFTS_DRAWER_OPEN,
    PASSES_READ,
    PASSES_MANAGE,
    PASSES_GRANT,
    PASSES_REFUND,
    PASSES_VOID,
    PRICING_READ,
    PRICING_MANAGE,
    ORDERS_ON_ACCOUNT,
    CREDIT_MANAGE,
    CREDIT_WRITE_OFF,
    TAX_MANAGE,
    REPORTS_READ,
    REPORTS_FINANCIAL,
    ORG_BILLING,
    INVENTORY_READ,
    INVENTORY_RECEIVE,
    INVENTORY_COUNT,
    INVENTORY_WASTE,
    INVENTORY_ADJUST,
    INVENTORY_MANAGE,
    INVENTORY_REPORTS,
    INVENTORY_SUPPLIES,
    BOOT_VIEW,
    BOOT_MANAGE,
    BOOT_EDIT_IMAGE,
    BOOT_WIPE,
];
/**
 * Every permission checked at platform scope. Platform roles are bundles of `hq:*` permissions
 * (spec/07 §7) plus `audit:read`, which is the one organisation permission that also has a
 * platform reading: HQ sees every organisation's log (spec/07 §11).
 */
export const PLATFORM_PERMISSIONS = [
    AUDIT_READ,
    HQ_ORGANIZATIONS,
    HQ_PLAYERS,
    HQ_PLAYERS_READ,
    HQ_SETTINGS,
    HQ_CATALOG,
    HQ_RELEASES,
    HQ_PAYMENTS,
    HQ_SUBSCRIPTIONS,
    HQ_LEADS,
];
/** Every permission the server enforces, at any scope, once each. */
export const PERMISSIONS = [...new Set([...ORGANIZATION_PERMISSIONS, ...PLATFORM_PERMISSIONS])];
/**
 * What each built-in organisation role holds by default.
 *
 * `owner` is "everything in the organisation" (spec/07 §6.2), so it is written as the whole list
 * rather than as a copy of it: a permission added to the catalogue is an owner's the moment it
 * exists, and cannot be forgotten here.
 */
export const ORGANIZATION_ROLE_GRANTS = {
    owner: ORGANIZATION_PERMISSIONS,
    // F-ORG-01: a manager runs the venue day to day and keeps its profile, hours and photos right.
    // Creating and archiving a venue stay the owner's — a venue is a line on the organisation's
    // subscription (spec/14 §4.1), and closing one is not an operational decision.
    //
    // F-ORG-03: a manager runs the floor — "everything operational" (spec/07 §6.2) — so they define the
    // classes the venue prices by and arrange the plan. Nobody below them changes either: a class is a
    // price tier, and the plan is what every screen in the venue draws.
    //
    // F-IAM-08: "staff below manager" (spec/07 §6.2) — the staff screen, bounded by the rule above,
    // so a manager invites, moves and removes cashiers, floor, kitchen and technicians, and not another
    // manager or an owner. The accountant is not below the manager either: it holds `tax:manage`, a
    // write the manager does not have (spec/07 §6.3, F-PAY-11), so only an owner grants or moves it.
    //
    // F-IAM-07: dealing with a player who has to be kept out is running the venue — "everything
    // operational" (spec/07 §6.2) — so a manager bans, at the venues they work at, and across the
    // organisation only when they work at all of them (spec/07 §3.5). Nobody below a manager does: a
    // cashier who needs a player kept out asks one. The narrowest reading of "staff with
    // `players:ban`", and a question on the F-IAM-07 pull request.
    //
    // F-ORG-02: a manager reads the settings screens and changes the operational groups among the
    // registered ones — today `settings:locker` (the power schedule; and the closing action, which
    // `venues:update` already covers). `settings:notifications` and `settings:all` stay the owner's:
    // the first holds only the organisation-wide sign-in code channels, which carry a cost, and the
    // second the second-factor rule. The feature that registers a venue notification setting decides
    // whether managers get the group.
    //
    // F-ORG-05, F-ORG-06: the manager runs the customer side too — the player record with the phone
    // number and address in full (a win-back offer and a dispute both need them), and its notes and tags.
    //
    // F-CMP-02: recording a staff document ID check is front-desk work, so the manager and the cashier
    // do it — the cashier of the issue's first acceptance criterion — and see the ID checks tab and
    // its photos, each view audited. The floor, kitchen, technician ("no customer data beyond names on
    // the floor") and accountant do not: a player who has to show an ID is sent to the desk
    // (spec/05 §3.2 check 4).
    //
    // F-CMP-03: `settings:players` — the ID-check rule, the methods an organisation accepts and how
    // long a verification lasts — stays the owner's. Whether a venue checks ID is a compliance position
    // the organisation takes (spec/20 §6), which HQ may even lock, not a day-to-day operational knob;
    // an owner who wants a manager to hold it builds a custom role.
    //
    // F-ORG-04: `settings:kiosk` is the owner's. Its one setting today is `org.branding` — the
    // organisation's own colours on every venue's lock screen — which is the organisation's identity,
    // not the running of a venue. The kiosk's operational settings (attract media, sign-in order,
    // spec/13 §13) decide whether managers get the group when they are registered.
    manager: [
        VENUES_READ,
        VENUES_UPDATE,
        STATIONS_READ,
        STATIONS_MANAGE,
        // F-STN-16 … F-STN-18: a manager runs the live floor, so the drawer's live-ops — power, the
        // maintenance-mode toggle, and locking or unlocking a screen — are theirs. F-SES-20: and messaging
        // the player at a station.
        STATIONS_POWER,
        STATIONS_MAINTENANCE,
        // F-LCK-06 / F-LCK-09 (2026-10-01): the manager issues the recovery code that unlocks `station-cli undo`, and may
        // put a station into maintenance during a live session (the player is warned). Neither goes to the technician or
        // the floor: both open a PC a player could otherwise not reach.
        STATIONS_RECOVERY,
        STATIONS_MAINTENANCE_IN_SESSION,
        STATIONS_MESSAGE,
        // F-STN-11 (2026-10-02): clearing or snoozing a PC's health alerts — the manager runs the floor's PCs.
        STATIONS_ALERTS_CLEAR,
        FLOOR_READ,
        FLOOR_ARRANGE,
        // F-CAT-02: a manager runs the venue, so the library — which titles the venue offers, on which
        // classes and in which kiosk category — is theirs to curate.
        LIBRARY_READ,
        LIBRARY_MANAGE,
        MEMBERS_READ,
        MEMBERS_INVITE,
        MEMBERS_UPDATE,
        MEMBERS_ROLES,
        MEMBERS_REMOVE,
        PLAYERS_BAN,
        SETTINGS_READ,
        SETTINGS_LOCKER,
        // F-OPS-08/-09: the venue's printers, their routing and the cash drawer's kick (spec/07 §6.2 "printer and
        // hardware settings") — operational, so the manager's as well as the technician's.
        SETTINGS_HARDWARE,
        PLAYERS_READ,
        PLAYERS_READ_CONTACT,
        PLAYERS_NOTES,
        PLAYERS_ID_CHECK,
        // F-SES-01/-04/-10/-14/-26: running the floor is running sessions — a manager assigns players to
        // stations, ends sessions, reads their status and timeline, and grants comp with a reason.
        SESSIONS_START,
        SESSIONS_END,
        SESSIONS_READ,
        SESSIONS_COMP,
        // F-SES-16: the manager comps past the setting's caps (a limit on the manager role itself still holds).
        SESSIONS_COMP_UNLIMITED,
        // F-STN-16: locking a station's screen from the floor is a session action the manager holds.
        SESSIONS_LOCK,
        // F-SES-18: moving a running session across stations is a floor action.
        SESSIONS_MOVE,
        // F-SES-24: generating an offline code to start/extend/unlock a station while the venue is offline
        // is a floor action the roles that run sessions hold.
        SESSIONS_OFFLINE_CODE,
        // F-SES-25: a manager runs the floor, so calls for help are theirs to see and to answer.
        CALLS_READ,
        CALLS_MANAGE,
        // The Sessions settings group is operational — grace, comp limits and the PAYG rate.
        SETTINGS_SESSIONS,
        // F-FNB-01/-02/-03: a manager runs the venue's menu — its categories, products, variants and
        // modifiers, the prices, and the sold-out switch — and the F&B settings group.
        PRODUCTS_READ,
        PRODUCTS_MANAGE,
        PRODUCTS_AVAILABILITY,
        SETTINGS_FNB,
        // F-FNB / F-POS-01: a manager places food orders on the counter, reads the order list, and
        // cancels an unpaid one; and reads and advances the kitchen display (F-FNB-04).
        ORDERS_CREATE,
        ORDERS_READ,
        ORDERS_VOID,
        // F-POS-05/-08/-02: "everything operational" — a manager gives manual discounts, refunds (within
        // limits and, above the threshold, a second manager or the owner approves) and runs the discount
        // codes (spec/07 §6.2).
        ORDERS_DISCOUNT,
        ORDERS_REFUND,
        ORDERS_REFUND_APPROVE,
        DISCOUNTS_READ,
        DISCOUNTS_MANAGE,
        KITCHEN_READ,
        KITCHEN_UPDATE,
        // F-WAL-02: topping up a player's wallet at the counter is front-desk work. `settings:payments`
        // (the wallet's money knobs) stays the owner's, like the other money-sensitive settings groups.
        WALLET_TOPUP,
        // F-WAL-06/-07: refunding wallet cash and adjusting a wallet are supervisory money-back actions,
        // the manager's — like `orders:refund` and the manual discount, and capped the same way
        // (`wallet.adjustMaxAmount`, spec/07 §6.4). The cashier tops up but does not refund or adjust.
        WALLET_ADJUST,
        WALLET_REFUND,
        // F-OPS-01 … F-OPS-07: a manager runs the venue's day — "everything operational… reports"
        // (spec/07 §6.2). They open and close their own till, record cash movements, read every shift's
        // X/Z report, and close the business day once all shifts are closed (spec/13 §10). They close or
        // hand over a shift another member opened (`close_any`) — the cashier closes only their own.
        SHIFTS_OWN,
        SHIFTS_READ_ALL,
        SHIFTS_CASH_MOVEMENTS,
        SHIFTS_DAY_CLOSE,
        SHIFTS_CLOSE_ANY,
        // F-OPS-09: Open drawer — a no-sale kick, audited with the shift.
        SHIFTS_DRAWER_OPEN,
        // F-PRC-01/-04/-05/-06: a manager runs the pass catalogue — makes and edits the products, grants
        // a pass, and refunds or voids a holding (the supervisory money-back actions, like `orders:void`).
        PASSES_READ,
        PASSES_MANAGE,
        PASSES_GRANT,
        PASSES_REFUND,
        PASSES_VOID,
        // F-PRC-10 … F-PRC-12: "everything operational: prices" (spec/07 §6.2) — the manager reads and publishes the
        // venue's rate card, as it already edits the flat pay-as-you-go rate (`settings:sessions`).
        PRICING_READ,
        PRICING_MANAGE,
        // F-POS-07: the manager runs the customer side, so it takes on-account tenders and sets a player's
        // credit limit. Writing off money owed (`credit:write_off`) stays the owner's alone.
        ORDERS_ON_ACCOUNT,
        CREDIT_MANAGE,
        // F-CAT-04: "everything operational … reports" (spec/07 §6.2) — the Games played report.
        REPORTS_READ,
        // F-ANL-01: the Reports overview's revenue and the canonical metrics — the manager closes the day already.
        REPORTS_FINANCIAL,
        // F-FNB-07 … F-FNB-13 (ADR-0040, 2026-10-07): "everything operational: … stock" (spec/07 §6.2) — every stock action,
        // its definitions and recipes, and its costs and margins.
        INVENTORY_READ,
        INVENTORY_RECEIVE,
        INVENTORY_COUNT,
        INVENTORY_WASTE,
        INVENTORY_ADJUST,
        INVENTORY_MANAGE,
        INVENTORY_REPORTS,
        INVENTORY_SUPPLIES,
        // Clavian Boot (ADR-0063, spec/25 §14): the manager sees the venue's boot servers, images and PCs, edits an image on
        // a PC and force-wipes write-back and shader caches — running the floor's PCs. Pairing a boot server, making,
        // restoring and merging images, and the PCs' licensed seats (`boot:manage`) stay the owner's.
        BOOT_VIEW,
        BOOT_EDIT_IMAGE,
        BOOT_WIPE,
    ],
    // Every console screen shows the venue — its name, its timezone, whether it is open — so every
    // role reads the profile. Reading is not editing: to them it is read-only.
    //
    // The floor and the classes are read by the roles that work the floor or sell on it: the cashier
    // sells by class, the floor staff and the technician work the live floor (spec/07 §6.2). The
    // kitchen sees tickets, and the accountant reports, and neither needs the plan.
    //
    // F-ORG-05, F-ORG-06: the front desk finds the player it is serving and writes the note ("prefers PC
    // 14, pays cash") — the cashier of spec/13 §8's acceptance criteria. Not the contact details in
    // full: a receipt or a payment link goes to the player's phone from the server, and the desk
    // searches by the number the player gives it, so masked is enough (spec/20 §6). The floor, the
    // kitchen, the technician ("no customer data beyond names on the floor") and the accountant are
    // not given the player record at all; an owner who wants them to have it builds a custom role.
    // F-SES: the cashier is the front desk — it assigns players to stations, ends their sessions,
    // reads them, and grants comp (bounded by the role's comp limits, spec/07 §6.4).
    cashier: [
        VENUES_READ,
        STATIONS_READ,
        STATIONS_MESSAGE,
        FLOOR_READ,
        PLAYERS_READ,
        PLAYERS_NOTES,
        PLAYERS_ID_CHECK,
        SESSIONS_START,
        SESSIONS_END,
        SESSIONS_READ,
        SESSIONS_COMP,
        // F-STN-16: the front desk locks and unlocks a station's screen from the floor.
        SESSIONS_LOCK,
        // F-SES-18: moving a running session across stations is a floor action.
        SESSIONS_MOVE,
        // F-SES-24: the front desk generates an offline code to start/extend/unlock a station when offline.
        SESSIONS_OFFLINE_CODE,
        // F-SES-25: the front desk sees and answers calls for help.
        CALLS_READ,
        CALLS_MANAGE,
        // F-FNB / F-POS-01: the front desk sells food — it reads the menu, places orders and reads them.
        // Cancelling a placed order (orders:void) and running the menu stay with the manager.
        PRODUCTS_READ,
        ORDERS_CREATE,
        ORDERS_READ,
        // F-FNB-03 (owner, 2026-10-01): the front desk marks a product sold out when it runs out — the
        // availability switch only; editing the product (`products:manage`) stays the manager's.
        PRODUCTS_AVAILABILITY,
        // F-POS-05: "small comps and discounts within limits" (spec/07 §6.2) — the cashier gives manual
        // discounts, capped by the role limit of `pos.manualDiscountMaxBps`. Refunds stay the manager's.
        ORDERS_DISCOUNT,
        // F-WAL-02: the front desk takes a player's top-up at the counter.
        WALLET_TOPUP,
        // F-OPS-01/-02/-03: the cashier is the till — "open and close own shift" (spec/07 §6.2). It
        // counts the opening float, records pay-ins, pay-outs and drops, and closes with the blind count.
        // Reading every shift's report (`read_all`) and closing the day (`day_close`) stay the manager's.
        SHIFTS_OWN,
        SHIFTS_CASH_MOVEMENTS,
        // F-OPS-09: the cashier is the till, so it opens the drawer outside a sale ("Open drawer"), audited.
        SHIFTS_DRAWER_OPEN,
        // F-PRC-01/-06: the front desk reads the pass catalogue and grants a pass (free time, like comp),
        // and sells one through `orders:create` above. Editing the catalogue, refunds and voids stay the
        // manager's.
        PASSES_READ,
        PASSES_GRANT,
        // F-POS-07: the front desk sells on account to a registered player within their limit. Setting the
        // limit (`credit:manage`) and writing money off (`credit:write_off`) stay with the manager and owner.
        ORDERS_ON_ACCOUNT,
        // F-FNB-09 (owner, 2026-10-07, ADR-0040 §16): the front desk takes deliveries, counts the shelves and records waste.
        // Corrections (`adjust`), definitions and recipes (`manage`) and the costs (`reports`) stay with the manager.
        INVENTORY_READ,
        INVENTORY_RECEIVE,
        INVENTORY_COUNT,
        INVENTORY_WASTE,
    ],
    // Floor staff work the live floor: they start and end sessions and read them, but comp — which
    // moves value — stays with the cashier and the manager. F-STN-16/-17: working the floor is powering
    // stations (restart, wake) and locking a screen; taking a machine out of service (maintenance) is
    // the technician's and the manager's. F-SES-20: they message the player at a station.
    // F-FNB-09 (owner, 2026-10-07, ADR-0040 §16): like the front desk, floor staff take deliveries, count and record waste —
    // quantities only: no stock values (`inventory:reports`), so "no money" still holds.
    floor: [VENUES_READ, STATIONS_READ, STATIONS_MESSAGE, FLOOR_READ, SESSIONS_START, SESSIONS_END, SESSIONS_READ, SESSIONS_LOCK, SESSIONS_MOVE, SESSIONS_OFFLINE_CODE, CALLS_READ, CALLS_MANAGE, STATIONS_POWER, INVENTORY_READ, INVENTORY_RECEIVE, INVENTORY_COUNT, INVENTORY_WASTE],
    // F-FNB-04: the kitchen runs the display — it reads and advances tickets — reads the menu to make
    // items, and marks a product sold out when it runs out (products:availability, spec/13 §6.1).
    kitchen: [VENUES_READ, PRODUCTS_READ, PRODUCTS_AVAILABILITY, KITCHEN_READ, KITCHEN_UPDATE],
    // A technician reads a session to support the player on the machine, but does not run it. F-STN-17/-18:
    // keeping the machines healthy is theirs — powering a station (restart, wake) and putting one into or
    // out of maintenance mode; F-SES-20: they message the player at the station they are helping.
    // F-CAT-02: "Stations, library, updates" (spec/07 §6.3, §6.2) — the technician keeps the machines and
    // what runs on them, so the venue library is theirs to curate alongside the manager.
    // F-OPS-08: "printer and hardware settings (`settings:hardware`)" (spec/07 §6.2) — the printers and their routing.
    // F-STN-11 (2026-10-02): clearing or snoozing a PC's health alerts once it is looked at — keeping the machines is theirs.
    // F-FNB-11 (owner, 2026-10-07, ADR-0040 §16): the venue's supplies — spare keyboards, cables, cleaning products — are
    // theirs to receive, issue, count and adjust (`inventory:supplies`, supply items only); food and retail stay out.
    technician: [VENUES_READ, STATIONS_READ, STATIONS_MESSAGE, STATIONS_ALERTS_CLEAR, FLOOR_READ, LIBRARY_READ, LIBRARY_MANAGE, SESSIONS_READ, CALLS_READ, CALLS_MANAGE, STATIONS_POWER, STATIONS_MAINTENANCE, SETTINGS_HARDWARE, INVENTORY_READ, INVENTORY_SUPPLIES],
    // F-PAY-11: the accountant answers for the organisation's tax, so recording a venue's registration —
    // its tax mode and GSTIN, a dated, audited change (spec/06 §10.4) — is theirs, alongside the owner's.
    // F-CAT-04: the accountant reads reports (spec/07 §6.2 "read-only reports") — the Games played report.
    // F-ANL-01: and the financial reports — the Reports overview (spec/17 §4.1).
    // F-PRC-10: the accountant reads the rate card the play revenue was priced by.
    // F-FNB-12 (ADR-0040 §16, 2026-10-07): and the stock — what it is worth, what was used and wasted, the margins.
    accountant: [VENUES_READ, TAX_MANAGE, REPORTS_READ, REPORTS_FINANCIAL, PRICING_READ, INVENTORY_READ, INVENTORY_REPORTS],
};
/**
 * What each platform role holds. `hq_owner` is "everything at platform level" (spec/07 §7); the
 * `maintainer` has no business data access without a break-glass grant (spec/07 §8), and the others
 * gain a permission when an endpoint in their area of the HQ console needs one.
 */
export const PLATFORM_ROLE_GRANTS = {
    hq_owner: PLATFORM_PERMISSIONS,
    // F-ORG-01 rule 6: "HQ sets Featured, Partner and Sponsored" — the organisations-and-venues area,
    // which spec/07 §7 gives `hq_admin`. `hq_support` reads organisations and venues and writes
    // nothing, so it is not granted the area.
    //
    // F-IAM-07: the platform ban (`hq:players`) is `hq_owner`'s alone. spec/07 §7 gives `hq_admin`
    // organisations, venues, subscriptions, catalogue, announcements, loyalty and reports — not people
    // — and `hq_support` writes nothing; keeping a person off the whole Platform is the heaviest thing
    // HQ can do to one. The narrowest reading, and a question on the F-IAM-07 pull request.
    //
    // F-ORG-02: `hq:settings` — platform defaults and locks — is `hq_owner`'s alone for now. spec/07
    // §7 gives no other platform role the platform settings, and a lock binds every venue on the
    // platform; F-HQ-38 (#200), which builds the screen, decides who else holds it.
    //
    // F-HQ-28: `hq:players_read` — the platform player search and one player's record, contact details
    // in full — is held by `hq_owner`, `hq_admin` and `hq_support`: the product owner's decision
    // (2026-09-22, on #479), which widens both roles. Support finds the person it is helping; neither
    // role bans, because `hq:players` stays `hq_owner`'s alone (#438).
    //
    // F-CAT-01, F-CAT-07: `hq:catalog` — the curated global game catalogue (spec/14 §6) — is held by
    // `hq_content`, whose spec/07 §7 area is "Game catalogue, news and announcements, kiosk media", and
    // by `hq_admin`, which spec/07 §7 lists the catalogue among. `hq_support` reads and writes nothing
    // of it, so it is not granted the area.
    //
    // F-STN-12: `hq:releases` — station releases and their staged rollouts (spec/14 §12) — is held by `hq_admin`
    // (platform operations) besides `hq_owner`. A rollout changes the software on every venue's PCs, so neither
    // `hq_support` nor `hq_content` holds it.
    //
    // F-HQ-39: `hq:leads` — the sales pipeline (spec/14 §16) — is held by `hq_admin`, whose area is onboarding organisations,
    // besides `hq_owner`. Converting a lead also needs `hq:organizations`, which `hq_admin` holds.
    //
    // F-BOOT-17: `hq:subscriptions` — add-ons and their prices (spec/14 §4.5) — is `hq_owner`'s alone for now, like
    // `hq:payments`: it decides what an organisation is charged and what it may use. spec/07 §7 lists subscriptions
    // under `hq_admin` and `hq_finance`; granting them is the owner's call (an open question on F-BOOT-17).
    hq_admin: [HQ_ORGANIZATIONS, HQ_PLAYERS_READ, HQ_CATALOG, HQ_RELEASES, HQ_LEADS],
    hq_finance: [],
    hq_support: [HQ_PLAYERS_READ],
    hq_content: [HQ_CATALOG],
    maintainer: [],
};
/**
 * The effective permissions of one member in one organisation.
 *
 * A built-in role reads the table above. Anything else is a custom role, whose grants are rows the
 * caller has already read from `auth.organization_role` — filtered here to permissions that exist,
 * so a row naming a permission nothing enforces grants nothing.
 */
export function permissionsForOrganizationRole(role, customRoleGrants = []) {
    if (isOrganizationRole(role))
        return new Set(ORGANIZATION_ROLE_GRANTS[role]);
    return new Set(ORGANIZATION_PERMISSIONS.filter((permission) => customRoleGrants.includes(permission)));
}
/** The effective platform permissions of a platform role; none for anything that is not one. */
export function permissionsForPlatformRole(role) {
    return isPlatformRole(role) ? new Set(PLATFORM_ROLE_GRANTS[role]) : new Set();
}
//# sourceMappingURL=index.js.map